// Enfermería (Fase 3, decisión de Sue: "MSP + nota SOAPIE") — lógica pura
// compartida por 020 ampliado, 022 Kardex, 120 grilla horaria y la nota de
// turno SOAPIE. Sin React ni MedplumClient: todo lo de acá se prueba en
// enfermeria.test.ts.
//
// Fuentes primarias: docs/msp/hcu-form-020/022/120-2021 (SNS-MSP/HCU-form.*/2021).
//
// ---------------------------------------------------------------------------
// CodeSystem local `http://logiacare.ec/fhir/CodeSystem/enfermeria-020`
// ---------------------------------------------------------------------------
// LOINC SOLO donde el código está verificado en node_modules/@medplum/
// definitions (vital signs de FHIR: 8310-5, 8867-4, 9279-1, 59408-5,
// 85354-9/8480-6/8462-4, 29463-7, 8302-2 — ya en uso en vitalSigns.ts — y
// 9843-4, perímetro cefálico, del perfil http://hl7.org/fhir/StructureDefinition/headcircum).
// Todo lo demás del 020 usa este CodeSystem local (NO se aplica como recurso
// CodeSystem en Medplum: Observation.code no se valida contra la base).
//   Categorías (Observation.category, secciones D/E/F del 020):
//     ingesta-eliminacion | cuidados-generales | dispositivos
//   Sección C:  perimetro-abdominal (cm) · antropometria-otros (texto)
//   Sección D:  ingreso-enteral · ingreso-parenteral · ingreso-oral (mL)
//               eliminacion-orina · eliminacion-drenaje · eliminacion-vomito ·
//               eliminacion-diarrea · eliminacion-otros (mL; "especifique" en note)
//               dieta-prescrita (texto) · numero-comidas · numero-micciones ·
//               numero-deposiciones (enteros)
//               Los TOTAL y el BALANCE HÍDRICO TOTAL NO se guardan: se calculan
//               con `calcularBalanceHidrico`.
//   Sección E:  aseo · bano (sí/no) · reposo · posicion · cuidados-otros (texto)
//   Sección F:  via-central · via-periferica · sonda-nasogastrica ·
//               sonda-vesical (fecha de colocación) · dispositivo-otros
//               (fecha; "especifique" en note)
// Turno: extensión `http://logiacare.ec/fhir/StructureDefinition/turno-enfermeria`
// (valueCode AM|PM|HS) y día de enfermería elegido:
// `http://logiacare.ec/fhir/StructureDefinition/dia-enfermeria` (valueDate) en
// cada Observation del 020 — effectiveDateTime queda como la hora real del
// registro (un HS se puede cargar a las 08:05 del día siguiente sin caer en el
// día equivocado). En la nota SOAPIE van en Composition.event (code + period).
import type {
  CodeableConcept,
  Composition,
  CompositionSection,
  Encounter,
  Extension,
  MedicationAdministration,
  MedicationRequest,
  Observation,
  Patient,
  Practitioner,
  Procedure,
  Reference,
} from '@medplum/fhirtypes';

type RefPaciente = Reference<Patient>;
type RefEncounter = Reference<Encounter>;
type RefPracticante = Reference<Practitioner>;

export const SISTEMA_ENFERMERIA_020 = 'http://logiacare.ec/fhir/CodeSystem/enfermeria-020';
export const SISTEMA_TURNO = 'http://logiacare.ec/fhir/CodeSystem/turno-enfermeria';
export const EXT_TURNO = 'http://logiacare.ec/fhir/StructureDefinition/turno-enfermeria';
export const EXT_DIA_ENFERMERIA = 'http://logiacare.ec/fhir/StructureDefinition/dia-enfermeria';
/** Nota de turno SOAPIE — NO es un formulario MSP (no está en el Anexo 1): código propio. */
export const SISTEMA_NOTA_TURNO = 'http://logiacare.ec/fhir/CodeSystem/nota-turno-enfermeria';
export const CODIGO_NOTA_SOAPIE = 'soapie';
/** Mismo system que el identifier de los Questionnaire MSP (calidad-hc.ts) — marca los Procedure del 120 para poder contarlos. */
export const SISTEMA_FORMULARIOS_MSP = 'http://logiacare.ec/fhir/formularios-msp';

const LOINC = 'http://loinc.org';
const UCUM = 'http://unitsofmeasure.org';

export const VITAL_SIGNS_CATEGORY: CodeableConcept = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'vital-signs', display: 'Vital Signs' }],
};

// ---------------------------------------------------------------------------
// Turnos y "día de enfermería"
// ---------------------------------------------------------------------------
// El HCU-form.120 numera las filas 8, 9 … 23, 0 … 7: el día de enfermería
// arranca a las 08:00 y termina a las 07:59 del día siguiente. Los turnos
// AM/PM/HS del 020 se reparten dentro de ese día: AM 08:00–13:59,
// PM 14:00–19:59, HS 20:00–07:59. El turno se elige explícito en cada
// formulario; esto solo da el valor por defecto según la hora.
export type Turno = 'AM' | 'PM' | 'HS';
export const TURNOS: readonly Turno[] = ['AM', 'PM', 'HS'];
export const HORA_INICIO_DIA = 8;
/** Filas del HCU-form.120 (y columnas del Kardex): 8..23, 0..7. */
export const HORAS_DEL_DIA: readonly number[] = Array.from({ length: 24 }, (_, i) => (HORA_INICIO_DIA + i) % 24);

export function turnoDeHora(hora: number): Turno {
  if (hora >= 8 && hora < 14) return 'AM';
  if (hora >= 14 && hora < 20) return 'PM';
  return 'HS';
}

function aIsoLocal(fecha: Date): string {
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, '0');
  const d = String(fecha.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** `YYYY-MM-DD` (hora local) del día de enfermería al que pertenece `fecha` — antes de las 08:00 cuenta como el día anterior. */
export function diaEnfermeriaDe(fecha: Date): string {
  const copia = new Date(fecha);
  if (copia.getHours() < HORA_INICIO_DIA) copia.setDate(copia.getDate() - 1);
  return aIsoLocal(copia);
}

/** `YYYY-MM-DD HH:mm` (hora local) en que se hizo el registro — para el PDF de la nota de turno, que antes no imprimía la hora. */
export function horaDeRegistro(fecha: Date): string {
  return `${aIsoLocal(fecha)} ${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;
}

/**
 * Ejecuta `fn` una sola vez con éxito: llamadas repetidas (o simultáneas) devuelven el mismo resultado. Si `fn` falla no cachea el
 * error, así un reintento vuelve a intentarlo. Sirve para crear un recurso en un paso reintentable sin duplicarlo.
 */
export function crearUnaVez<T>(fn: () => Promise<T>): () => Promise<T> {
  let pendiente: Promise<T> | undefined;
  return () => {
    if (!pendiente) {
      pendiente = fn().catch((err: unknown) => {
        pendiente = undefined;
        throw err;
      });
    }
    return pendiente;
  };
}

/** Fecha real (hora local) de la fila `hora` del día de enfermería `dia` — las horas 0..7 caen en el día calendario siguiente. */
export function fechaDeCelda(dia: string, hora: number, minuto = 0): Date {
  const [y, m, d] = dia.split('-').map(Number);
  const fecha = new Date(y!, m! - 1, d!, hora, minuto, 0, 0);
  if (hora < HORA_INICIO_DIA) fecha.setDate(fecha.getDate() + 1);
  return fecha;
}

/** `[inicio, fin)` del día de enfermería — fin exclusivo (08:00 del día siguiente). */
export function rangoDiaEnfermeria(dia: string): { inicio: Date; fin: Date } {
  const inicio = fechaDeCelda(dia, HORA_INICIO_DIA);
  const fin = new Date(inicio);
  fin.setDate(fin.getDate() + 1);
  return { inicio, fin };
}

/** `[start, end)` del turno dentro del día de enfermería — para Composition.event.period. */
export function periodoDeTurno(dia: string, turno: Turno): { start: string; end: string } {
  const desde = { AM: 8, PM: 14, HS: 20 }[turno];
  // HS termina a las 08:00 del día siguiente = fin del día de enfermería
  // (fechaDeCelda(dia, 8) sería las 08:00 del MISMO día, antes del inicio).
  const fin = turno === 'HS' ? rangoDiaEnfermeria(dia).fin : fechaDeCelda(dia, { AM: 14, PM: 20 }[turno]);
  return { start: fechaDeCelda(dia, desde).toISOString(), end: fin.toISOString() };
}

/** Turno + día de enfermería elegidos en el formulario (ver cabecera). */
export function extensionesTurno(turno: Turno, dia: string): Extension[] {
  return [
    { url: EXT_TURNO, valueCode: turno },
    { url: EXT_DIA_ENFERMERIA, valueDate: dia },
  ];
}

/** Día de enfermería elegido (extensión); sin ella (registros viejos) se deriva de la hora efectiva. */
export function diaDeObservacion(o: Observation): string | undefined {
  const guardado = o.extension?.find((e) => e.url === EXT_DIA_ENFERMERIA)?.valueDate;
  if (guardado) return guardado;
  return o.effectiveDateTime ? diaEnfermeriaDe(new Date(o.effectiveDateTime)) : undefined;
}

/** Turno guardado en la extensión; si no hay (registros anteriores a este cambio), se deriva de la hora efectiva. */
export function turnoDeObservacion(o: Observation): Turno | undefined {
  const guardado = o.extension?.find((e) => e.url === EXT_TURNO)?.valueCode;
  if (guardado === 'AM' || guardado === 'PM' || guardado === 'HS') return guardado;
  return o.effectiveDateTime ? turnoDeHora(new Date(o.effectiveDateTime).getHours()) : undefined;
}

// ---------------------------------------------------------------------------
// Definición de las medidas del 020 (secciones C, D, E, F)
// ---------------------------------------------------------------------------
export type SeccionMedida = 'C' | 'D' | 'E' | 'F';
export type TipoMedida = 'ml' | 'cantidad' | 'entero' | 'texto' | 'booleano' | 'fecha';

export interface DefMedida {
  readonly clave: string;
  readonly etiqueta: string;
  readonly seccion: SeccionMedida;
  readonly tipo: TipoMedida;
  readonly coding: { system: string; code: string; display: string };
  readonly unidad?: string;
  readonly unidadUcum?: string;
  /** "OTROS ESPECIFIQUE" del formulario oficial — el texto va a Observation.note. */
  readonly especifique?: boolean;
}

function local(code: string, display: string): DefMedida['coding'] {
  return { system: SISTEMA_ENFERMERIA_020, code, display };
}

export const CATEGORIAS_020: Record<Exclude<SeccionMedida, 'C'>, CodeableConcept> = {
  D: { coding: [local('ingesta-eliminacion', 'Ingesta - eliminación / balance hídrico')] },
  E: { coding: [local('cuidados-generales', 'Cuidados generales')] },
  F: { coding: [local('dispositivos', 'Fecha de colocación de dispositivos médicos')] },
};

export const MEDIDAS_020: readonly DefMedida[] = [
  // C. Medidas antropométricas — category vital-signs (se ven en la pestaña Signos Vitales).
  { clave: 'peso', etiqueta: 'Peso', seccion: 'C', tipo: 'cantidad', coding: { system: LOINC, code: '29463-7', display: 'Body weight' }, unidad: 'kg', unidadUcum: 'kg' },
  { clave: 'talla', etiqueta: 'Talla', seccion: 'C', tipo: 'cantidad', coding: { system: LOINC, code: '8302-2', display: 'Body height' }, unidad: 'cm', unidadUcum: 'cm' },
  { clave: 'perimetro-cefalico', etiqueta: 'Perímetro cefálico', seccion: 'C', tipo: 'cantidad', coding: { system: LOINC, code: '9843-4', display: 'Head Occipital-frontal circumference' }, unidad: 'cm', unidadUcum: 'cm' },
  { clave: 'perimetro-abdominal', etiqueta: 'Perímetro abdominal', seccion: 'C', tipo: 'cantidad', coding: local('perimetro-abdominal', 'Perímetro abdominal'), unidad: 'cm', unidadUcum: 'cm' },
  { clave: 'antropometria-otros', etiqueta: 'Otros (especifique)', seccion: 'C', tipo: 'texto', coding: local('antropometria-otros', 'Otras medidas antropométricas') },
  // D. Ingesta - eliminación / balance hídrico.
  { clave: 'ingreso-enteral', etiqueta: 'Ingreso enteral', seccion: 'D', tipo: 'ml', coding: local('ingreso-enteral', 'Ingreso enteral'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'ingreso-parenteral', etiqueta: 'Ingreso parenteral', seccion: 'D', tipo: 'ml', coding: local('ingreso-parenteral', 'Ingreso parenteral'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'ingreso-oral', etiqueta: 'Ingreso vía oral', seccion: 'D', tipo: 'ml', coding: local('ingreso-oral', 'Ingreso vía oral'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'eliminacion-orina', etiqueta: 'Orina', seccion: 'D', tipo: 'ml', coding: local('eliminacion-orina', 'Eliminación: orina'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'eliminacion-drenaje', etiqueta: 'Drenaje', seccion: 'D', tipo: 'ml', coding: local('eliminacion-drenaje', 'Eliminación: drenaje'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'eliminacion-vomito', etiqueta: 'Vómito', seccion: 'D', tipo: 'ml', coding: local('eliminacion-vomito', 'Eliminación: vómito'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'eliminacion-diarrea', etiqueta: 'Diarreas', seccion: 'D', tipo: 'ml', coding: local('eliminacion-diarrea', 'Eliminación: diarreas'), unidad: 'mL', unidadUcum: 'mL' },
  { clave: 'eliminacion-otros', etiqueta: 'Otras eliminaciones (especifique)', seccion: 'D', tipo: 'ml', coding: local('eliminacion-otros', 'Eliminación: otros'), unidad: 'mL', unidadUcum: 'mL', especifique: true },
  { clave: 'dieta-prescrita', etiqueta: 'Dieta prescrita', seccion: 'D', tipo: 'texto', coding: local('dieta-prescrita', 'Dieta prescrita') },
  { clave: 'numero-comidas', etiqueta: 'Número de comidas', seccion: 'D', tipo: 'entero', coding: local('numero-comidas', 'Número de comidas') },
  { clave: 'numero-micciones', etiqueta: 'Número de micciones', seccion: 'D', tipo: 'entero', coding: local('numero-micciones', 'Número de micciones') },
  { clave: 'numero-deposiciones', etiqueta: 'Número de deposiciones', seccion: 'D', tipo: 'entero', coding: local('numero-deposiciones', 'Número de deposiciones') },
  // E. Cuidados generales.
  // Aseo: el instructivo pide "el tipo de aseo" (texto); antes era un visto sí/no. Los Observation viejos (valueBoolean) se siguen leyendo.
  { clave: 'aseo', etiqueta: 'Aseo (tipo)', seccion: 'E', tipo: 'texto', coding: local('aseo', 'Aseo') },
  { clave: 'bano', etiqueta: 'Baño', seccion: 'E', tipo: 'booleano', coding: local('bano', 'Baño') },
  { clave: 'reposo', etiqueta: 'Reposo (absoluto, relativo o ambulatorio)', seccion: 'E', tipo: 'texto', coding: local('reposo', 'Reposo') },
  { clave: 'posicion', etiqueta: 'Posición y hora del cambio', seccion: 'E', tipo: 'texto', coding: local('posicion', 'Posición') },
  { clave: 'cuidados-otros', etiqueta: 'Otros (especifique)', seccion: 'E', tipo: 'texto', coding: local('cuidados-otros', 'Otros cuidados generales') },
  // F. Fecha de colocación de dispositivos médicos.
  { clave: 'via-central', etiqueta: 'Vía central', seccion: 'F', tipo: 'fecha', coding: local('via-central', 'Fecha de colocación: vía central') },
  { clave: 'via-periferica', etiqueta: 'Vía periférica', seccion: 'F', tipo: 'fecha', coding: local('via-periferica', 'Fecha de colocación: vía periférica') },
  { clave: 'sonda-nasogastrica', etiqueta: 'Sonda nasogástrica', seccion: 'F', tipo: 'fecha', coding: local('sonda-nasogastrica', 'Fecha de colocación: sonda nasogástrica') },
  { clave: 'sonda-vesical', etiqueta: 'Sonda vesical', seccion: 'F', tipo: 'fecha', coding: local('sonda-vesical', 'Fecha de colocación: sonda vesical') },
  { clave: 'dispositivo-otros', etiqueta: 'Otros dispositivos (especifique)', seccion: 'F', tipo: 'fecha', coding: local('dispositivo-otros', 'Fecha de colocación: otro dispositivo'), especifique: true },
];

const INGRESOS = ['ingreso-enteral', 'ingreso-parenteral', 'ingreso-oral'] as const;
const ELIMINACIONES = ['eliminacion-orina', 'eliminacion-drenaje', 'eliminacion-vomito', 'eliminacion-diarrea', 'eliminacion-otros'] as const;

export interface ContextoObservacion {
  readonly subject: RefPaciente;
  readonly encounter?: RefEncounter;
  readonly turno: Turno;
  /** Día de enfermería `YYYY-MM-DD` elegido en el formulario. */
  readonly dia: string;
  readonly efectivo: string;
  readonly performer?: RefPracticante;
  /** Extensiones adicionales de cada Observation del registro (día de internación / postquirúrgico del 020). */
  readonly extensiones?: readonly Extension[];
}

/** Valor tal como lo tipea el formulario: número/texto/booleano/fecha `YYYY-MM-DD`. */
export type ValorMedida = number | string | boolean;

/** Arma el Observation de UNA medida del 020 (secciones C–F). `undefined` si el valor está vacío. */
export function observacionDeMedida(def: DefMedida, valor: ValorMedida | '' | undefined, ctx: ContextoObservacion, especifique?: string): Observation | undefined {
  if (valor === '' || valor === undefined || valor === false) return undefined;
  const base: Observation = {
    resourceType: 'Observation',
    status: 'final',
    category: [def.seccion === 'C' ? VITAL_SIGNS_CATEGORY : CATEGORIAS_020[def.seccion]],
    code: { coding: [def.coding], text: def.etiqueta },
    subject: ctx.subject,
    encounter: ctx.encounter,
    effectiveDateTime: ctx.efectivo,
    performer: ctx.performer ? [ctx.performer] : undefined,
    extension: [...extensionesTurno(ctx.turno, ctx.dia), ...(ctx.extensiones ?? [])],
    note: def.especifique && especifique?.trim() ? [{ text: especifique.trim() }] : undefined,
  };
  switch (def.tipo) {
    case 'ml':
    case 'cantidad': {
      const n = Number(valor);
      if (!Number.isFinite(n) || n < 0) return undefined;
      return { ...base, valueQuantity: { value: n, unit: def.unidad, system: UCUM, code: def.unidadUcum } };
    }
    case 'entero': {
      const n = Number(valor);
      if (!Number.isInteger(n) || n < 0) return undefined;
      return { ...base, valueInteger: n };
    }
    case 'booleano':
      return { ...base, valueBoolean: valor === true };
    case 'fecha':
      return typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor) ? { ...base, valueDateTime: valor } : undefined;
    case 'texto':
      return typeof valor === 'string' && valor.trim() ? { ...base, valueString: valor.trim() } : undefined;
  }
}

/** Texto legible del valor de un Observation del 020 (para pares del PDF y tablas). */
export function textoValorObservacion(o: Observation): string {
  if (o.valueQuantity) return `${o.valueQuantity.value} ${o.valueQuantity.unit ?? ''}`.trim();
  if (o.valueInteger !== undefined) return String(o.valueInteger);
  if (o.valueBoolean !== undefined) return o.valueBoolean ? 'Sí' : 'No';
  if (o.valueDateTime) return o.valueDateTime;
  if (o.valueString) return o.valueString;
  if (o.component?.length) {
    return o.component
      .map((c) => (c.valueQuantity ? `${c.valueQuantity.value} ${c.valueQuantity.unit ?? ''}`.trim() : ''))
      .filter(Boolean)
      .join(' / ');
  }
  return '—';
}

// ---------------------------------------------------------------------------
// Balance hídrico (sección D): totales por turno y por 24 h
// ---------------------------------------------------------------------------
export interface RegistroLiquido {
  readonly codigo: string;
  readonly ml: number;
  readonly turno: Turno;
  readonly dia: string;
}

export interface TotalesLiquidos {
  ingresos: number;
  eliminaciones: number;
  /** ingresos − eliminaciones (positivo = balance positivo). */
  balance: number;
}

export interface BalanceDia {
  dia: string;
  porTurno: Record<Turno, TotalesLiquidos>;
  total24h: TotalesLiquidos;
}

const CODIGOS_INGRESO = new Set<string>(INGRESOS);
const CODIGOS_ELIMINACION = new Set<string>(ELIMINACIONES);

/** Extrae los registros en mL de la sección D (ignora el resto de Observations). */
export function registrosLiquidosDe(observaciones: readonly Observation[]): RegistroLiquido[] {
  const registros: RegistroLiquido[] = [];
  for (const o of observaciones) {
    const coding = o.code?.coding?.find((c) => c.system === SISTEMA_ENFERMERIA_020);
    const ml = o.valueQuantity?.value;
    if (!coding?.code || ml === undefined || !o.effectiveDateTime) continue;
    if (!CODIGOS_INGRESO.has(coding.code) && !CODIGOS_ELIMINACION.has(coding.code)) continue;
    const turno = turnoDeObservacion(o);
    const dia = diaDeObservacion(o);
    if (!turno || !dia) continue;
    registros.push({ codigo: coding.code, ml, turno, dia });
  }
  return registros;
}

function totalesVacios(): TotalesLiquidos {
  return { ingresos: 0, eliminaciones: 0, balance: 0 };
}

function sumar(t: TotalesLiquidos, r: RegistroLiquido): void {
  if (!Number.isFinite(r.ml) || r.ml < 0) return;
  if (CODIGOS_INGRESO.has(r.codigo)) t.ingresos += r.ml;
  else if (CODIGOS_ELIMINACION.has(r.codigo)) t.eliminaciones += r.ml;
  t.balance = t.ingresos - t.eliminaciones;
}

/** Totales de ingresos, eliminaciones y balance por turno y por día de enfermería (24 h), días más recientes primero. */
export function calcularBalanceHidrico(registros: readonly RegistroLiquido[]): BalanceDia[] {
  const porDia = new Map<string, BalanceDia>();
  for (const r of registros) {
    let dia = porDia.get(r.dia);
    if (!dia) {
      dia = { dia: r.dia, porTurno: { AM: totalesVacios(), PM: totalesVacios(), HS: totalesVacios() }, total24h: totalesVacios() };
      porDia.set(r.dia, dia);
    }
    sumar(dia.porTurno[r.turno], r);
    sumar(dia.total24h, r);
  }
  return [...porDia.values()].sort((a, b) => b.dia.localeCompare(a.dia));
}

// ---------------------------------------------------------------------------
// 022 — Kardex (MedicationAdministration)
// ---------------------------------------------------------------------------
export function etiquetaMedicamento(mr: Pick<MedicationRequest, 'medicationCodeableConcept' | 'medicationReference' | 'id'>): string {
  return mr.medicationCodeableConcept?.text ?? mr.medicationCodeableConcept?.coding?.[0]?.display ?? mr.medicationReference?.display ?? mr.id ?? '—';
}

/** "Dosis, vía, frecuencia" del MSP — la receta (construirReceta.ts) guarda la pauta como `dosageInstruction[0].text`. */
export function pautaDe(mr: MedicationRequest): string {
  const d = mr.dosageInstruction?.[0];
  if (!d) return '';
  if (d.text) return d.text;
  const dosis = d.doseAndRate?.[0]?.doseQuantity;
  return [dosis ? `${dosis.value ?? ''} ${dosis.unit ?? ''}`.trim() : '', d.route?.text ?? d.route?.coding?.[0]?.display ?? '', d.timing?.code?.text ?? ''].filter(Boolean).join(', ');
}

export interface EntradaAdministracion {
  readonly request: MedicationRequest;
  readonly subject: RefPaciente;
  readonly encounter?: RefEncounter;
  readonly fecha: Date;
  readonly administrado: boolean;
  /** Obligatorio si `administrado === false` (statusReason). */
  readonly motivo?: string;
  readonly dosis?: string;
  readonly via?: string;
  readonly performer?: RefPracticante;
}

const DOSIS_NUMERICA = /^\s*(\d+(?:[.,]\d+)?)\s*(\S.*)?$/;

/**
 * MedicationAdministration de una celda del Kardex. `dosage` solo si la dosis
 * se puede leer como número (invariante mad-1: dosage exige dose o rate); si
 * no, dosis y vía quedan como texto en `note` para no perder el dato.
 * R4 usa `context` (no `encounter`) para el Encounter.
 */
export function construirAdministracion(e: EntradaAdministracion): MedicationAdministration {
  const dosis = e.dosis?.trim() ?? '';
  const via = e.via?.trim() ?? '';
  const numerica = DOSIS_NUMERICA.exec(dosis);
  const notas: string[] = [];
  let dosage: MedicationAdministration['dosage'];
  if (numerica) {
    dosage = {
      text: [dosis, via].filter(Boolean).join(' · '),
      dose: { value: Number(numerica[1]!.replace(',', '.')), unit: numerica[2]?.trim() || undefined },
      route: via ? { text: via } : undefined,
    };
  } else if (dosis || via) {
    notas.push([dosis ? `Dosis: ${dosis}` : '', via ? `Vía: ${via}` : ''].filter(Boolean).join(' · '));
  }
  return {
    resourceType: 'MedicationAdministration',
    status: e.administrado ? 'completed' : 'not-done',
    statusReason: !e.administrado && e.motivo?.trim() ? [{ text: e.motivo.trim() }] : undefined,
    medicationCodeableConcept: e.request.medicationCodeableConcept ?? (e.request.medicationReference ? undefined : { text: etiquetaMedicamento(e.request) }),
    medicationReference: e.request.medicationCodeableConcept ? undefined : e.request.medicationReference,
    subject: e.subject,
    context: e.encounter,
    effectiveDateTime: e.fecha.toISOString(),
    performer: e.performer ? [{ actor: e.performer }] : undefined,
    request: { reference: `MedicationRequest/${e.request.id}` },
    dosage,
    note: notas.length ? notas.map((text) => ({ text })) : undefined,
  };
}

/** Celda (`requestId|hora`) de una administración ya guardada — incluye las del 022 viejo (sin performer ni dosage). */
export function celdaDeAdministracion(ma: MedicationAdministration): { requestId: string; hora: number } | undefined {
  // Id tras `MedicationRequest/` (una referencia absoluta o con `_history` no debe dar un id equivocado).
  const requestId = /MedicationRequest\/([^/]+)/.exec(ma.request?.reference ?? '')?.[1];
  if (!requestId || !ma.effectiveDateTime) return undefined;
  return { requestId, hora: new Date(ma.effectiveDateTime).getHours() };
}

/**
 * Las prescripciones que pertenecen a la estancia de internación abierta
 * (2026-09-13, decisión de Sue). El Kardex buscaba `MedicationRequest?status=active`
 * sin acotar: entraban recetas AMBULATORIAS, que nadie cierra nunca (quedan
 * `active` para siempre), y la grilla del 022 terminaba pidiendo administrar
 * medicación que no es de la internación.
 *
 * El vínculo real es `MedicationRequest.encounter`, que pone `construirReceta`
 * (formulario/recetas/construirReceta.ts) con el Encounter que traía la URL al
 * prescribir. Sin estancia abierta devuelve `[]` a propósito — el llamante
 * muestra el aviso en vez de listar todo; y una prescripción vieja sin
 * `encounter` (recetas anteriores a que el wizard lo propagara) tampoco entra:
 * no hay forma de saber si pertenece a esta internación.
 */
export function prescripcionesDeEstancia<T extends Pick<MedicationRequest, 'encounter'>>(
  prescripciones: readonly T[],
  estanciaRef: string | null | undefined,
): T[] {
  if (!estanciaRef) return [];
  return prescripciones.filter((mr) => mr.encounter?.reference === estanciaRef);
}

export function responsableDe(performer: { actor?: Reference }[] | Reference[] | undefined): string {
  const primero = performer?.[0];
  if (!primero) return '—';
  const ref = 'actor' in primero ? primero.actor : (primero as Reference);
  return ref?.display ?? ref?.reference ?? '—';
}

// ---------------------------------------------------------------------------
// 120 — Intervenciones de enfermería (Procedure)
// ---------------------------------------------------------------------------
export const CATEGORIA_PROCEDURE_120: CodeableConcept = {
  coding: [{ system: SISTEMA_FORMULARIOS_MSP, code: '120', display: 'Intervenciones de Enfermería (SNS-MSP/HCU-form.120/2021)' }],
};

export function construirProcedure120(e: {
  readonly intervencion: string;
  readonly fecha: Date;
  readonly subject: RefPaciente;
  readonly encounter?: RefEncounter;
  readonly performer?: RefPracticante;
  readonly reasonReference?: Procedure['reasonReference'];
}): Procedure {
  return {
    resourceType: 'Procedure',
    status: 'completed',
    category: CATEGORIA_PROCEDURE_120,
    code: { text: e.intervencion.trim() },
    subject: e.subject,
    encounter: e.encounter,
    performedDateTime: e.fecha.toISOString(),
    performer: e.performer ? [{ actor: e.performer }] : undefined,
    reasonReference: e.reasonReference,
  };
}

// ---------------------------------------------------------------------------
// Nota de turno SOAPIE (Composition) — NO es formulario MSP
// ---------------------------------------------------------------------------
export type ClaveSoapie = 'S' | 'O' | 'A' | 'P' | 'I' | 'E';
export const SECCIONES_SOAPIE: readonly { clave: ClaveSoapie; titulo: string }[] = [
  { clave: 'S', titulo: 'Subjetivo' },
  { clave: 'O', titulo: 'Objetivo' },
  { clave: 'A', titulo: 'Análisis' },
  { clave: 'P', titulo: 'Planificación' },
  { clave: 'I', titulo: 'Intervención' },
  { clave: 'E', titulo: 'Evaluación' },
];

/** LOINC 34746-8 "Nurse Note" verificado en valuesets.json de @medplum/definitions; el código local es el que se usa para buscar/contar. */
export const TIPO_NOTA_SOAPIE: CodeableConcept = {
  coding: [
    { system: SISTEMA_NOTA_TURNO, code: CODIGO_NOTA_SOAPIE, display: 'Nota de turno de enfermería (SOAPIE)' },
    { system: LOINC, code: '34746-8', display: 'Nurse Note' },
  ],
  text: 'Nota de turno de enfermería (SOAPIE)',
};

const SIN_REGISTRO = 'Sin registro en este turno.';

export function narrativa(texto: string): CompositionSection['text'] {
  const escapado = texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
  return { status: 'generated', div: `<div xmlns="http://www.w3.org/1999/xhtml"><p>${escapado}</p></div>` };
}

export function tituloNotaSoapie(turno: Turno, dia: string): string {
  return `Nota de turno de enfermería — ${turno} ${dia}`;
}

// ---------------------------------------------------------------------------
// Lectura de la nota de turno (2026-09-13) — hasta acá el Composition se
// CREABA y nadie lo leía: la enfermera del turno siguiente no podía ver la
// nota anterior (hallazgo del revisor funcional). Todo lo de abajo es la
// inversa exacta de `construirNotaSoapie`/`narrativa`, sin DOM (corre igual en
// el navegador y en los tests de node).
// ---------------------------------------------------------------------------

/** Filtro de búsqueda de las notas SOAPIE de un paciente, más recientes primero. */
export function filtroNotasSoapie(patientId: string, cantidad = 50): Record<string, string> {
  return {
    patient: `Patient/${patientId}`,
    type: `${SISTEMA_NOTA_TURNO}|${CODIGO_NOTA_SOAPIE}`,
    _sort: '-date',
    _count: String(cantidad),
  };
}

/** `true` si el Composition es una nota de turno SOAPIE (y no, p. ej., una epicrisis). */
export function esNotaSoapie(c: { type?: Composition['type'] }): boolean {
  return !!c.type?.coding?.some((coding) => coding.system === SISTEMA_NOTA_TURNO && coding.code === CODIGO_NOTA_SOAPIE);
}

/** Texto plano de la narrativa XHTML que arma `narrativa()`: `<br/>` vuelve a ser salto de línea y las entidades se desescapan. */
export function textoDeNarrativa(div: string | undefined): string {
  if (!div) return '';
  return div
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .trim();
}

/** El turno guardado en `Composition.event` (ver `construirNotaSoapie`). */
export function turnoDeNota(c: Pick<Composition, 'event'>): Turno | undefined {
  const code = c.event?.[0]?.code?.[0]?.coding?.find((coding) => coding.system === SISTEMA_TURNO)?.code;
  return code === 'AM' || code === 'PM' || code === 'HS' ? code : undefined;
}

export interface SeccionLeida {
  readonly clave: ClaveSoapie;
  readonly titulo: string;
  readonly texto: string;
}

export interface NotaSoapieLeida {
  readonly id?: string;
  /** `Composition.date` tal cual (ISO) — la hora en que se firmó la nota. */
  readonly fecha?: string;
  readonly turno?: Turno;
  /** Día de enfermería, derivado de `event[0].period.start`. */
  readonly dia?: string;
  readonly autor?: string;
  readonly titulo?: string;
  readonly secciones: SeccionLeida[];
}

/**
 * Pasa un `Composition` de nota de turno a algo mostrable. Las 6 secciones
 * salen SIEMPRE en el orden S-O-A-P-I-E aunque el recurso las traiga en otro
 * orden o le falte alguna (una nota vieja, o editada a mano por API).
 */
export function leerNotaSoapie(c: Composition): NotaSoapieLeida {
  const inicio = c.event?.[0]?.period?.start;
  return {
    id: c.id,
    fecha: c.date,
    turno: turnoDeNota(c),
    dia: inicio ? diaEnfermeriaDe(new Date(inicio)) : undefined,
    autor: c.author?.[0]?.display ?? c.author?.[0]?.reference,
    titulo: c.title,
    secciones: SECCIONES_SOAPIE.map(({ clave, titulo }) => {
      const seccion = c.section?.find((s) => s.code?.coding?.some((coding) => coding.system === SISTEMA_NOTA_TURNO && coding.code === clave));
      return { clave, titulo, texto: textoDeNarrativa(seccion?.text?.div) };
    }),
  };
}

/**
 * Composition de la nota de turno, siempre con las 6 secciones (cmp-1 exige
 * texto en cada una: las vacías llevan "Sin registro en este turno.").
 */
export function construirNotaSoapie(e: {
  readonly subject: RefPaciente;
  readonly encounter?: RefEncounter;
  /** Obligatorio: Composition.author es 1..*. */
  readonly autor: RefPracticante;
  readonly turno: Turno;
  readonly dia: string;
  readonly textos: Partial<Record<ClaveSoapie, string>>;
  readonly ahora: Date;
}): Composition {
  return {
    resourceType: 'Composition',
    status: 'final',
    type: TIPO_NOTA_SOAPIE,
    subject: e.subject,
    encounter: e.encounter,
    date: e.ahora.toISOString(),
    author: [e.autor],
    title: tituloNotaSoapie(e.turno, e.dia),
    event: [{ code: [{ coding: [{ system: SISTEMA_TURNO, code: e.turno }] }], period: periodoDeTurno(e.dia, e.turno) }],
    section: SECCIONES_SOAPIE.map(({ clave, titulo }) => ({
      title: titulo,
      code: { coding: [{ system: SISTEMA_NOTA_TURNO, code: clave, display: titulo }] },
      text: narrativa(e.textos[clave]?.trim() || SIN_REGISTRO),
    })),
  };
}
